import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import Text from 'particles/Text'
import Avatar from 'particles/Avatar'
import Icon, { IconName } from 'particles/Icon'
import styled from 'particles/themes/styled'
import { SubmissionState } from 'state/submissions'
import { SubmissionTags } from 'components/Tag'
import { selectPartiposterProfileById } from 'state/partiposter'
import qs from 'qs'
import { SearchParamObject } from 'components/utils/url'
import { PLATFORM_ICON_MAP } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import Tooltip from 'particles/Tooltip'
import { abbreviateNumber } from 'components/utils'
import { selectSearchablePlatforms, selectSidebarTask } from 'state/review'
import { PLATFORM_REDIRECT_URL } from 'services/numbers'
import { PLATFORM_ENUM } from 'services/api/constants'
import IconButton from 'particles/Button/IconButton'
import { useNewTranslation } from 'hooks'
import { copyToClipboard } from 'components/utils'
import { selectCountrySetting } from 'state/constants'

export interface PartiposterProfileProps {
    submission: SubmissionState
    submissionUuid: string
}
const UsernameWithTooltip: React.FC<{
    username: string | null
    availableWidth: number | null
}> = ({ username, availableWidth }) => {
    const textRef = useRef<HTMLDivElement>(null)

    const usernameText = (
        <EllipsisText maxWidth={availableWidth} ref={textRef} scale="bodyS500" color={'action'} textCursor="pointer">
            @{username}
        </EllipsisText>
    )

    if (availableWidth && textRef.current?.clientWidth && textRef.current.scrollWidth > availableWidth) {
        return <Tooltip label={`@${username}`}>{usernameText}</Tooltip>
    }

    return usernameText
}

const NewPartiposterProfile = ({ submission, submissionUuid }: PartiposterProfileProps) => {
    const [t] = useNewTranslation()
    const { campaign } = qs.parse(location.search) as SearchParamObject
    const campaignUuid: string = campaign as string
    const profile = useSelector(selectPartiposterProfileById(submission?.partiposterId))
    const countrySetting = useSelector(selectCountrySetting(profile.country))
    const identityRef = useRef<HTMLDivElement>(null)
    const followerCountRef = useRef<HTMLDivElement>(null)
    const [availableUsernameWidth, setAvailableUsernameWidth] = useState<number | null>(null)

    const searchablePlatforms = useSelector(selectSearchablePlatforms)
    const sidebarTask = useSelector(selectSidebarTask)

    // since this component is also rendered in a virtualized list
    // we need to calculate the available width of the username with a layout effect
    useLayoutEffect(() => {
        const calculateWidth = () => {
            if (identityRef.current?.clientWidth && followerCountRef.current?.clientWidth) {
                const width = identityRef.current.clientWidth - followerCountRef.current.clientWidth - 64
                setAvailableUsernameWidth(width)
            }
        }

        calculateWidth()

        const timeoutId = setTimeout(calculateWidth, 0)

        return () => clearTimeout(timeoutId)
    }, [])

    const platformProfileUrls = searchablePlatforms.map(platform => {
        const account = profile.social_media_accounts.filter(account => {
            return account.platform === platform
        })
        return account?.[0]?.profile_url ?? ''
    })

    // always display one social media, with IG as default for Other task type
    const socialMediaProfile = profile?.social_media_accounts?.find(account => {
        if (sidebarTask?.platform === PLATFORM_ENUM.OTHER) {
            return account.platform === PLATFORM_ENUM.IG
        }

        return account.platform === sidebarTask?.platform
    })

    const phone = useMemo(() => {
        if (!profile.phone) return ''
        if (!countrySetting?.prefix) return profile.phone
        return countrySetting.prefix + profile.phone
    }, [profile.phone, countrySetting])

    return (
        <ProfileContainer className="partiposter-profile">
            <div className="identity" ref={identityRef}>
                <Avatar
                    profile={{
                        name: profile.name,
                        uuid: profile.uuid,
                        photo: profile.photo,
                    }}
                    size="l"
                />
                <div
                    className="identity-info"
                    style={{
                        width: identityRef.current?.clientWidth ? identityRef.current.clientWidth - 48 : 'auto',
                    }}
                >
                    <Text scale="bodyM700" className="name">
                        {profile.name} ({profile.age})
                    </Text>
                    {socialMediaProfile && (
                        <SocialMediaListContainer className="social-media-list">
                            <div className="social-media-item">
                                <Tooltip
                                    label={
                                        phone
                                            ? t('general.tooltip.clickToCopy', { 'text-to-copy': phone })
                                            : t('general.tooltip.noPhoneNumber')
                                    }
                                    placement="top"
                                >
                                    <IconButton
                                        icon="phoneList"
                                        form="icon"
                                        size="xs"
                                        onClick={e => {
                                            e.stopPropagation()
                                            copyToClipboard(phone, t)
                                        }}
                                        isDisabled={!phone}
                                    />
                                </Tooltip>
                                <div className="separator"></div>
                                <div className="social-media-follower-count" ref={followerCountRef}>
                                    <Icon
                                        name={PLATFORM_ICON_MAP[socialMediaProfile.platform] as IconName}
                                        color="primary"
                                    />
                                    <Text scale="bodyS500" color={'primary'}>
                                        {socialMediaProfile.follower_count
                                            ? abbreviateNumber(socialMediaProfile.follower_count)
                                            : '-'}
                                    </Text>
                                </div>
                                <div className="separator"></div>
                                <a
                                    className="social-media"
                                    onClick={e => e.stopPropagation()}
                                    href={
                                        platformProfileUrls[0] ||
                                        `${PLATFORM_REDIRECT_URL[Number(socialMediaProfile.platform) as PLATFORM_ENUM]}${socialMediaProfile.username}${socialMediaProfile.platform === PLATFORM_ENUM.XIAOHONGSHU ? '' : '/'}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <UsernameWithTooltip
                                        username={socialMediaProfile.username}
                                        availableWidth={availableUsernameWidth}
                                    />
                                </a>
                            </div>
                        </SocialMediaListContainer>
                    )}
                </div>
            </div>
            <div>
                <SubmissionTags profileData={profile} submissionUuid={submissionUuid} campaignUuid={campaignUuid} />
            </div>
        </ProfileContainer>
    )
}

export default NewPartiposterProfile

const ProfileContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[300]};
    .identity {
        display: flex;
        gap: ${({ theme }) => theme.spacing[200]};
        align-items: center;

        .name {
            text-align: left;
        }

        .social-media-list {
            display: flex;
            gap: ${({ theme }) => theme.spacing[200]};

            .separator {
                border-left: 1px solid ${({ theme }) => theme.colors.border.primary};
                height: 20px;
            }

            .social-media-item {
                display: flex;
                gap: ${({ theme }) => theme.spacing[200]};
                align-items: center;

                .social-media-follower-count {
                    display: flex;
                    gap: ${({ theme }) => theme.spacing[100]};
                    align-items: center;
                }
            }

            .social-media {
                text-decoration: none;
            }
        }
    }
`

const SocialMediaListContainer = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[100]};
    flex-wrap: wrap;
    align-items: center;
`

const EllipsisText = styled(Text)<{ maxWidth: number | null }>`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px` : '100%')};
`
